import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { request } from "@/api/client";
import { fetchArtistSignals, fetchThreads } from "@/api/observability";
import type { ArtistSignalItem, ObservabilityThread } from "@/api/observability";
import { fetchExecutiveGoals } from "@/api/executive";
import type { ExecutiveGoal } from "@/api/executive";
import { listEventSettlements } from "@/api/events/settlement";
import type { EventSettlementSummary } from "@/api/events/settlement";
import { fetchPressReleases } from "@/api/publicist";
import type { PressRelease } from "@/types/publicist";
import { getMomentumTone, formatRelativeTime } from "@/pages/observabilityUtils";
import { agentUiBridge } from "@/lib/agentUiBridge";

// ─── Shared primitives ────────────────────────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
      {label}
    </h3>
  );
}

function StatTile({ label, value, accent }: { label: string; value: string | number; accent?: string }) {
  return (
    <div className="rounded-lg border border-border/50 bg-background/50 p-3 text-center">
      <p className={`text-xl font-bold tabular-nums ${accent ?? "text-foreground"}`}>{value}</p>
      <p className="mt-0.5 text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}

function QuickLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="block rounded-md border border-border/50 bg-background/40 px-3 py-2 text-xs text-foreground/80 transition-colors hover:border-primary/40 hover:bg-background/70 hover:text-foreground"
    >
      {label}
    </Link>
  );
}

function QuickAction({ agentName, prompt, label }: { agentName: string; prompt: string; label: string }) {
  return (
    <button
      type="button"
      onClick={() => agentUiBridge.emit({ type: "chat.prefill", agent: agentName, text: prompt, focusChat: true })}
      className="block w-full rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-left text-xs text-primary/80 transition-colors hover:border-primary/60 hover:bg-primary/10 hover:text-primary"
    >
      {label}
    </button>
  );
}

/** Horizontal segmented bar: segments = [{ color, pct, label }] */
function SegmentBar({ segments }: { segments: Array<{ color: string; pct: number; label: string; count: number }> }) {
  const nonZero = segments.filter((s) => s.count > 0);
  if (nonZero.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted/40">
        {nonZero.map((s) => (
          <div key={s.label} className={`${s.color} transition-all`} style={{ width: `${s.pct}%` }} title={`${s.label}: ${s.count}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {nonZero.map((s) => (
          <div key={s.label} className="flex items-center gap-1">
            <div className={`h-1.5 w-1.5 rounded-full ${s.color}`} />
            <span className="text-[10px] text-muted-foreground">{s.count} {s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Single horizontal bar showing a value relative to max */
function MiniBar({ pct, color, label, value }: { pct: number; color: string; label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-foreground/70">{value}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted/40">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
      </div>
    </div>
  );
}

function AgentThreadBadge({ threads, agentName }: { threads: ObservabilityThread[]; agentName: string }) {
  const filtered = threads.filter((t) => t.agent?.toLowerCase().includes(agentName));
  const active = filtered.filter((t) => t.active).length;
  if (filtered.length === 0) return null;
  return (
    <div className="flex gap-2">
      <StatTile label="Active" value={active} accent={active > 0 ? "text-emerald-400" : undefined} />
      <StatTile label="Total" value={filtered.length} />
    </div>
  );
}

// ─── Booking ──────────────────────────────────────────────────────────────────

type LeadRow = { id: string; status: string; score?: number; company?: string };

function BookingPanel() {
  const [threads, setThreads] = useState<ObservabilityThread[]>([]);
  const [leads, setLeads] = useState<LeadRow[]>([]);

  useEffect(() => {
    fetchThreads().then(setThreads).catch(() => {});
    request<unknown>("/booking/leads/recommendations?limit=30")
      .then((res) => {
        const items = Array.isArray(res) ? res : ((res as Record<string, unknown>)?.recommendations as unknown[]) ?? [];
        setLeads(
          (items as Array<Record<string, unknown>>).map((r) => ({
            id: String(r.id ?? ""),
            status: String(r.status ?? "pending"),
            score: typeof r.score === "number" ? r.score : undefined,
            company: String(
              r.lead_metadata && typeof r.lead_metadata === "object"
                ? (r.lead_metadata as Record<string, unknown>).company_name ?? ""
                : ""
            ),
          }))
        );
      })
      .catch(() => {});
  }, []);

  // Pipeline stage breakdown
  const stageCounts = leads.reduce<Record<string, number>>((acc, l) => {
    const s = l.status || "pending";
    acc[s] = (acc[s] ?? 0) + 1;
    return acc;
  }, {});
  const total = leads.length;
  const pipelineSegments = [
    { label: "new", color: "bg-sky-400", count: stageCounts["new"] ?? stageCounts["pending"] ?? 0 },
    { label: "contacted", color: "bg-violet-400", count: stageCounts["contacted"] ?? 0 },
    { label: "sent", color: "bg-amber-400", count: stageCounts["sent"] ?? 0 },
    { label: "confirmed", color: "bg-emerald-400", count: stageCounts["confirmed"] ?? 0 },
    { label: "lost", color: "bg-rose-400", count: stageCounts["lost"] ?? stageCounts["rejected"] ?? 0 },
  ].map((s) => ({ ...s, pct: total > 0 ? (s.count / total) * 100 : 0 }));

  // Top leads by score
  const topLeads = [...leads]
    .filter((l) => l.score !== undefined)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    .slice(0, 3);

  const maxScore = Math.max(...topLeads.map((l) => l.score ?? 0), 0.01);

  return (
    <div className="space-y-5">
      <div>
        <SectionHeader label="Thread Activity" />
        <AgentThreadBadge threads={threads} agentName="booking" />
      </div>

      {total > 0 && (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <SectionHeader label="Lead Pipeline" />
            <span className="text-[10px] text-muted-foreground">{total} total</span>
          </div>
          <SegmentBar segments={pipelineSegments} />
        </div>
      )}

      {topLeads.length > 0 && (
        <div>
          <SectionHeader label="Top Leads" />
          <div className="space-y-2">
            {topLeads.map((l) => (
              <MiniBar
                key={l.id}
                label={l.company || `Lead #${l.id}`}
                value={`${Math.round((l.score ?? 0) * 100)}%`}
                pct={maxScore > 0 ? ((l.score ?? 0) / maxScore) * 100 : 0}
                color="bg-primary/60"
              />
            ))}
          </div>
        </div>
      )}

      <div>
        <SectionHeader label="Quick Actions" />
        <div className="space-y-1.5">
          <QuickAction agentName="booking" prompt="Review my top pending leads and recommend the best ones to pursue this week." label="Review top leads" />
          <QuickAction agentName="booking" prompt="Search for venues in my target markets with availability in the next 60 days." label="Find venues" />
        </div>
      </div>

      <div>
        <SectionHeader label="Quick Links" />
        <div className="space-y-1.5">
          <QuickLink to="/booking" label="Booking Pipeline" />
          <QuickLink to="/crm" label="CRM Contacts" />
        </div>
      </div>
    </div>
  );
}

// ─── Manager ─────────────────────────────────────────────────────────────────

const GOAL_STATUS_TONE: Record<string, string> = {
  complete: "text-emerald-400 bg-emerald-500/10 border-emerald-400/30",
  completed: "text-emerald-400 bg-emerald-500/10 border-emerald-400/30",
  in_progress: "text-sky-400 bg-sky-500/10 border-sky-400/30",
  pending: "text-amber-400 bg-amber-500/10 border-amber-400/30",
  blocked: "text-rose-400 bg-rose-500/10 border-rose-400/30",
};

function goalTone(status: string) {
  return GOAL_STATUS_TONE[status.toLowerCase()] ?? "text-muted-foreground bg-muted border-border/40";
}

function ManagerPanel() {
  const [threads, setThreads] = useState<ObservabilityThread[]>([]);
  const [goals, setGoals] = useState<ExecutiveGoal[]>([]);
  const [settlements, setSettlements] = useState<EventSettlementSummary[]>([]);

  useEffect(() => {
    fetchThreads().then(setThreads).catch(() => {});
    fetchExecutiveGoals().then((r) => setGoals(r.goals)).catch(() => {});
    listEventSettlements().then((rows) => setSettlements(rows.slice(0, 5))).catch(() => {});
  }, []);

  const openGoals = goals.filter((g) => g.status !== "complete" && g.status !== "completed");
  const doneGoals = goals.filter((g) => g.status === "complete" || g.status === "completed");
  const goalPct = goals.length > 0 ? (doneGoals.length / goals.length) * 100 : 0;

  // Settlement P&L bars
  const profits = settlements.map((s) => (typeof s.profit === "number" ? s.profit : Number(s.profit) || 0));
  const maxAbs = Math.max(...profits.map(Math.abs), 1);

  return (
    <div className="space-y-5">
      <div>
        <SectionHeader label="Thread Activity" />
        <AgentThreadBadge threads={threads} agentName="manager" />
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <SectionHeader label="Goals" />
          {goals.length > 0 && (
            <span className="text-[10px] text-muted-foreground">{doneGoals.length}/{goals.length} done</span>
          )}
        </div>
        {goals.length > 0 && (
          <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-muted/40">
            <div className="h-full rounded-full bg-emerald-400 transition-all" style={{ width: `${goalPct}%` }} />
          </div>
        )}
        {goals.length === 0 ? (
          <p className="text-xs text-muted-foreground">No goals set — ask the manager to create some.</p>
        ) : (
          <ul className="space-y-1.5">
            {openGoals.slice(0, 4).map((goal) => (
              <li key={goal.id} className="rounded-md border border-border/40 bg-background/40 px-3 py-2 text-xs">
                <div className="flex items-start justify-between gap-2">
                  <p className="flex-1 text-foreground/90">{goal.title}</p>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[9px] uppercase font-semibold ${goalTone(goal.status)}`}>
                    {goal.status.replace(/_/g, " ")}
                  </span>
                </div>
                {goal.deadline && (
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    Due {new Date(goal.deadline).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {settlements.length > 0 && (
        <div>
          <SectionHeader label="Settlement P&L" />
          <div className="space-y-2">
            {settlements.map((s, i) => {
              const profit = profits[i];
              const pct = (Math.abs(profit) / maxAbs) * 100;
              return (
                <MiniBar
                  key={s.event_id}
                  label={s.name ?? s.event_id}
                  value={`${profit >= 0 ? "+" : ""}$${Math.abs(profit).toLocaleString()}`}
                  pct={pct}
                  color={profit >= 0 ? "bg-emerald-400" : "bg-rose-400"}
                />
              );
            })}
          </div>
        </div>
      )}

      <div>
        <SectionHeader label="Quick Actions" />
        <div className="space-y-1.5">
          <QuickAction agentName="manager" prompt="Review the current goals and suggest priorities and next steps for this week." label="Review goal priorities" />
          <QuickAction agentName="manager" prompt="Summarize the recent show settlements and flag any financial issues." label="Settlement summary" />
        </div>
      </div>

      <div>
        <SectionHeader label="Quick Links" />
        <div className="space-y-1.5">
          <QuickLink to="/artists" label="Artist Roster" />
          <QuickLink to="/calendar" label="Tour Calendar" />
          <QuickLink to="/accounting" label="Settlements" />
        </div>
      </div>
    </div>
  );
}

// ─── Marketing ───────────────────────────────────────────────────────────────

function MarketingPanel() {
  const [threads, setThreads] = useState<ObservabilityThread[]>([]);
  const [signals, setSignals] = useState<ArtistSignalItem[]>([]);

  useEffect(() => {
    fetchThreads().then(setThreads).catch(() => {});
    fetchArtistSignals().then(setSignals).catch(() => {});
  }, []);

  const topSignals = signals.slice(0, 5);
  const maxSignals = Math.max(...topSignals.map((s) => s.signals), 1);

  return (
    <div className="space-y-5">
      <div>
        <SectionHeader label="Thread Activity" />
        <AgentThreadBadge threads={threads} agentName="marketing" />
      </div>

      {topSignals.length > 0 && (
        <div>
          <SectionHeader label="Artist Pulse" />
          <div className="space-y-3">
            {topSignals.map((s) => (
              <div key={s.artist_id} className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-xs font-medium text-foreground">{s.name}</p>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-wide ${getMomentumTone(s.momentum)}`}>
                    {s.momentum}
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted/40">
                  <div
                    className={`h-full rounded-full transition-all ${s.momentum === "up" ? "bg-emerald-400" : s.momentum === "down" ? "bg-rose-400" : "bg-sky-400"}`}
                    style={{ width: `${(s.signals / maxSignals) * 100}%` }}
                  />
                </div>
                <p className="text-[10px] text-muted-foreground">
                  {s.signals} signal{s.signals !== 1 ? "s" : ""} · {s.agents.slice(0, 2).join(", ")}
                  {s.agents.length > 2 ? ` +${s.agents.length - 2}` : ""}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <SectionHeader label="Quick Actions" />
        <div className="space-y-1.5">
          {topSignals[0] && (
            <QuickAction
              agentName="marketing"
              prompt={`Draft a marketing brief for ${topSignals[0].name} based on their current momentum.`}
              label={`Brief for ${topSignals[0].name}`}
            />
          )}
          <QuickAction agentName="marketing" prompt="Give me a full artist momentum report across all active artists." label="Momentum report" />
        </div>
      </div>

      <div>
        <SectionHeader label="Quick Links" />
        <div className="space-y-1.5">
          <QuickLink to="/marketing" label="Marketing Console" />
          <QuickLink to="/artists" label="Artist Roster" />
        </div>
      </div>
    </div>
  );
}

// ─── Publicist ────────────────────────────────────────────────────────────────

function PublicistPanel() {
  const [threads, setThreads] = useState<ObservabilityThread[]>([]);
  const [releases, setReleases] = useState<PressRelease[]>([]);

  useEffect(() => {
    fetchThreads().then(setThreads).catch(() => {});
    fetchPressReleases().then((res) => setReleases(Array.isArray(res) ? res : [])).catch(() => {});
  }, []);

  // Status breakdown
  const statusCounts = releases.reduce<Record<string, number>>((acc, r) => {
    const s = String(r.status ?? "draft").toLowerCase();
    acc[s] = (acc[s] ?? 0) + 1;
    return acc;
  }, {});
  const total = releases.length;
  const pressSegments = [
    { label: "draft", color: "bg-muted-foreground/50", count: statusCounts["draft"] ?? 0 },
    { label: "submitted", color: "bg-amber-400", count: statusCounts["submitted"] ?? statusCounts["in_review"] ?? 0 },
    { label: "published", color: "bg-emerald-400", count: statusCounts["published"] ?? statusCounts["live"] ?? 0 },
  ].map((s) => ({ ...s, pct: total > 0 ? (s.count / total) * 100 : 0 }));

  const recentReleases = releases.slice(0, 4);

  return (
    <div className="space-y-5">
      <div>
        <SectionHeader label="Thread Activity" />
        <AgentThreadBadge threads={threads} agentName="publicist" />
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <SectionHeader label="Press Releases" />
          {total > 0 && <span className="text-[10px] text-muted-foreground">{total} total</span>}
        </div>
        {total > 0 && <SegmentBar segments={pressSegments} />}
        {total === 0 && <p className="text-xs text-muted-foreground">No releases yet.</p>}
      </div>

      {recentReleases.length > 0 && (
        <div>
          <SectionHeader label="Recent Releases" />
          <ul className="space-y-1.5">
            {recentReleases.map((r) => (
              <li key={r.id} className="rounded-md border border-border/40 bg-background/40 px-3 py-2 text-xs">
                <p className="truncate font-medium text-foreground">{r.title}</p>
                <p className="mt-0.5 text-[10px] capitalize text-muted-foreground">{r.status}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <SectionHeader label="Quick Actions" />
        <div className="space-y-1.5">
          <QuickAction agentName="publicist" prompt="Draft a press release for our most recent show announcement." label="Draft press release" />
          <QuickAction agentName="publicist" prompt="Summarize recent media coverage and sentiment across all active artists." label="Media coverage report" />
        </div>
      </div>

      <div>
        <SectionHeader label="Quick Links" />
        <div className="space-y-1.5">
          <QuickLink to="/pressroom" label="Pressroom" />
          <QuickLink to="/artists" label="Artist Roster" />
        </div>
      </div>
    </div>
  );
}

// ─── Email ────────────────────────────────────────────────────────────────────

type EmailLogRow = { id: unknown; direction: string; subject?: string; created_at?: string };

function EmailPanel() {
  const [threads, setThreads] = useState<ObservabilityThread[]>([]);
  const [logs, setLogs] = useState<EmailLogRow[]>([]);

  useEffect(() => {
    fetchThreads().then(setThreads).catch(() => {});
    request<unknown>("/email/logs?limit=15")
      .then((res) => {
        const items = Array.isArray(res) ? res : [];
        setLogs(
          (items as Array<Record<string, unknown>>).map((l) => ({
            id: l.id,
            direction: String(l.direction ?? ""),
            subject: l.subject ? String(l.subject) : undefined,
            created_at: l.created_at ? String(l.created_at) : undefined,
          }))
        );
      })
      .catch(() => {});
  }, []);

  const inbound = logs.filter((l) => l.direction === "inbound").length;
  const outbound = logs.filter((l) => l.direction === "outbound").length;
  const total = inbound + outbound;
  const recentLogs = logs.slice(0, 5);

  return (
    <div className="space-y-5">
      <div>
        <SectionHeader label="Thread Activity" />
        <AgentThreadBadge threads={threads} agentName="email" />
      </div>

      {total > 0 && (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <SectionHeader label="Volume (last 15)" />
          </div>
          <SegmentBar
            segments={[
              { label: "inbound", color: "bg-sky-400", count: inbound, pct: (inbound / total) * 100 },
              { label: "outbound", color: "bg-emerald-400", count: outbound, pct: (outbound / total) * 100 },
            ]}
          />
        </div>
      )}

      {recentLogs.length > 0 && (
        <div>
          <SectionHeader label="Recent Emails" />
          <ul className="space-y-1.5">
            {recentLogs.map((log) => (
              <li key={String(log.id)} className="rounded-md border border-border/40 bg-background/40 px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-1">
                  <span className={`rounded px-1.5 py-0.5 text-[9px] uppercase font-semibold ${log.direction === "inbound" ? "bg-sky-500/10 text-sky-400" : "bg-emerald-500/10 text-emerald-400"}`}>
                    {log.direction}
                  </span>
                  {log.created_at && (
                    <span className="text-[10px] text-muted-foreground">{formatRelativeTime(log.created_at)}</span>
                  )}
                </div>
                {log.subject && <p className="mt-1 truncate text-foreground/80">{log.subject}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <SectionHeader label="Quick Actions" />
        <div className="space-y-1.5">
          <QuickAction agentName="email" prompt="Show me all unanswered inbound emails that need a response." label="Show unanswered emails" />
          <QuickAction agentName="email" prompt="Draft an outreach email sequence for a new venue contact." label="Draft outreach sequence" />
        </div>
      </div>

      <div>
        <SectionHeader label="Quick Links" />
        <div className="space-y-1.5">
          <QuickLink to="/email" label="Email Inbox" />
          <QuickLink to="/crm" label="CRM Contacts" />
        </div>
      </div>
    </div>
  );
}

// ─── Podcast ──────────────────────────────────────────────────────────────────

type ActivityLogRow = { id: unknown; action_type: string; notes?: string; created_at?: string };

function PodcastPanel() {
  const [threads, setThreads] = useState<ObservabilityThread[]>([]);
  const [activity, setActivity] = useState<ActivityLogRow[]>([]);

  useEffect(() => {
    fetchThreads().then(setThreads).catch(() => {});
    request<unknown>("/crm/activity/log?limit=30")
      .then((res) => {
        const items = Array.isArray(res) ? res : ((res as Record<string, unknown>)?.activity as unknown[]) ?? [];
        const podcastRows = (items as Array<Record<string, unknown>>)
          .filter((r) => {
            const t = String(r.action_type ?? "").toLowerCase();
            return t.includes("podcast");
          })
          .slice(0, 8)
          .map((r) => ({
            id: r.id,
            action_type: String(r.action_type ?? ""),
            notes: r.notes ? String(r.notes) : undefined,
            created_at: r.created_at ? String(r.created_at) : undefined,
          }));
        setActivity(podcastRows);
      })
      .catch(() => {});
  }, []);

  const pitches = activity.filter((r) => r.action_type === "podcast_pitch").length;
  const confirmed = activity.filter((r) => r.action_type === "podcast_confirmed").length;

  return (
    <div className="space-y-5">
      <div>
        <SectionHeader label="Thread Activity" />
        <AgentThreadBadge threads={threads} agentName="podcast" />
      </div>

      {(pitches > 0 || confirmed > 0) && (
        <div>
          <SectionHeader label="Outreach Stats" />
          <div className="grid grid-cols-2 gap-2">
            <StatTile label="Pitched" value={pitches} />
            <StatTile label="Confirmed" value={confirmed} accent={confirmed > 0 ? "text-emerald-400" : undefined} />
          </div>
        </div>
      )}

      {activity.length > 0 && (
        <div>
          <SectionHeader label="Recent Podcast Activity" />
          <ul className="space-y-1.5">
            {activity.slice(0, 5).map((row) => (
              <li key={String(row.id)} className="rounded-md border border-border/40 bg-background/40 px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-1">
                  <span className={`rounded px-1.5 py-0.5 text-[9px] uppercase font-semibold ${
                    row.action_type === "podcast_confirmed"
                      ? "bg-emerald-500/10 text-emerald-400"
                      : "bg-violet-500/10 text-violet-400"
                  }`}>
                    {row.action_type.replace("podcast_", "")}
                  </span>
                  {row.created_at && (
                    <span className="text-[10px] text-muted-foreground">{formatRelativeTime(row.created_at)}</span>
                  )}
                </div>
                {row.notes && <p className="mt-1 truncate text-foreground/70">{row.notes}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {activity.length === 0 && (
        <p className="text-xs text-muted-foreground">No podcast pitches logged yet — ask the agent to find shows.</p>
      )}

      <div>
        <SectionHeader label="Quick Actions" />
        <div className="space-y-1.5">
          <QuickAction agentName="podcast" prompt="Find 5 podcast shows that would be a good fit for my artist and draft a pitch for the best one." label="Find & pitch shows" />
          <QuickAction agentName="podcast" prompt="Review my recent podcast outreach and suggest follow-ups for pitches that haven't been answered." label="Review outreach" />
        </div>
      </div>

      <div>
        <SectionHeader label="Quick Links" />
        <div className="space-y-1.5">
          <QuickLink to="/crm" label="CRM Contacts" />
          <QuickLink to="/artists" label="Artist Roster" />
        </div>
      </div>
    </div>
  );
}

// ─── Public export ────────────────────────────────────────────────────────────

const AGENT_LABELS: Record<string, string> = {
  booking: "Booking Agent",
  manager: "Manager Agent",
  marketing: "Marketing Agent",
  publicist: "Publicist Agent",
  email: "Email Agent",
  podcast: "Podcast Agent",
};

const AGENT_DESCRIPTION: Record<string, string> = {
  booking: "Finds and manages booking leads, venue searches, and show logistics.",
  manager: "Oversees artist schedules, tracks goals, and resolves settlements.",
  marketing: "Tracks artist momentum, plans campaigns, and drafts promotional content.",
  publicist: "Drafts press releases, tracks media coverage, and manages press contacts.",
  email: "Handles outreach, inbox triage, and follow-up sequences.",
  podcast: "Finds podcast opportunities, drafts pitches, and tracks outreach for guest appearances.",
};

export function AgentContextPanel({ agentName }: { agentName: string }) {
  const label = AGENT_LABELS[agentName] ?? agentName;
  const description = AGENT_DESCRIPTION[agentName];

  return (
    <div className="flex flex-col gap-4">
      {/* Agent identity header */}
      <div className="rounded-xl border border-border/60 bg-background/60 p-4">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        {description && <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{description}</p>}
      </div>

      {/* Agent-specific context */}
      <div className="rounded-xl border border-border/60 bg-background/60 p-4">
        {agentName === "booking" && <BookingPanel />}
        {agentName === "manager" && <ManagerPanel />}
        {agentName === "marketing" && <MarketingPanel />}
        {agentName === "publicist" && <PublicistPanel />}
        {agentName === "email" && <EmailPanel />}
        {agentName === "podcast" && <PodcastPanel />}
      </div>
    </div>
  );
}

export default AgentContextPanel;
